import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import uuid from 'react-uuid';
import { DocumentReferencesView } from 'api';
import { MAX_DELEGATION_DEPTH } from 'common/constants/delegation';
import { MAX_DOCUMENT_FILE_SIZE_ALLOWED, MAX_DOCUMENT_SIZE_IN_MB } from 'common/constants/documents';
import { OTHER } from 'common/constants/process-management';
import { FILE_TYPE_NOT_ALLOWED } from 'common/constants/user';
import { useDragAndDropFiles } from 'common/hooks/useDragAndDropFiles';
import type { RequestedFileFulfillmentStatus } from 'common/types/files';
import { ExtraActionsMenu } from 'components';
import { ConfirmationDialog } from 'components/Dialogs';
import { Action } from 'components/Dialogs/common/Dialogs.props';
import validFileTypes from 'components/Documents/validFileTypes';
import EnhancedTableHead from 'components/EnhancedTableHead';
import { EnhancedTableHeadColumn } from 'components/EnhancedTableHead/types';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import {
  ERROR_DISMISSING_REQUESTED_DOCUMENT,
  SUCCESS_DISMISSING_REQUESTED_DOCUMENT,
  SUCCESS_MODIFYING_INFORMATION_REQUEST,
} from 'dashboard409a/common/constants';
import { modifyExistingRequest } from 'dashboard409a/services/dashboard';
import SelectionModal from 'pages/RequestDocuments/components/SelectionModal';
import FilesService from 'services/documents';
import { useDocuments, useResponse } from 'services/hooks';
import theme from 'theme';
import { Logger } from 'utilities';
import AdminDelegateModal from './AdminDelegateModal';
import { CurrentRequestedDocumentTablesProps, DocumentProgress } from './types';
import { filterDelegatableQuestions } from '../util/filterInformationRequests';
import getFormattedDate from '../util/getFormattedDate';

const filesService = new FilesService();

const PREFIX = 'PendingDocumentTable';

const classes = {
  footer: `${PREFIX}-footer`,
  footerText: `${PREFIX}-footerText`,
  buttonContainer: `${PREFIX}-buttonContainer`,
  folder: `${PREFIX}-folder`,
  actionMenu: `${PREFIX}-actionMenu`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.footer}`]: {
    backgroundColor: theme?.palette?.grey?.[200],
    position: 'fixed',
    bottom: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  [`& .${classes.footerText}`]: {
    textAlign: 'center',
  },

  [`& .${classes.buttonContainer}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.folder}`]: {
    backgroundColor: theme?.palette?.grey?.[200],
    color: theme?.palette?.primary?.main,
    fontWeight: 'bold',
  },

  [`& .${classes.actionMenu}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}));

const StyledTableRow = styled(TableRow)({});
const StyledTableCell = styled(TableCell)({
  fontSize: theme.spacing(2),
});

const PendingDocumentTable = ({ documents, setUploadedFiles, unmetRequests, questions }: CurrentRequestedDocumentTablesProps) => {
  const [filesToUpload, setFilesToUpload] = useState<{ file: File; tmpId: string }[]>([]);
  const [openSelectionModal, setOpenSelectionModal] = useState(false);
  const [documentToRemove, setDocumentToRemove] = useState<DocumentProgress | null>(null);
  const [isOpenDismissDocumentRequestModal, setIsOpenDismissDocumentRequestModal] = useState(false);
  const [singleRequestedFileSelected, setSingleRequestedFileSelected] = useState<DocumentProgress | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<DocumentReferencesView[]>([]);
  const [isDelegateModalOpen, setIsDelegateModalOpen] = useState(false);
  const [documentToMarkNa, setDocumentToMarkNa] = useState<DocumentProgress | null>(null);
  const [naStatus, setNaStatus] = useState<RequestedFileFulfillmentStatus>('not_applicable');
  const [naNote, setNaNote] = useState('');
  const [isMarkingNa, setIsMarkingNa] = useState(false);

  const lastTaskId = documents?.id ?? null;

  const { currentDocuments, handleGetCurrentInformationRequests, task, taskPortalLinkData } = useContext(
    InformationDashboard409AContext
  );

  const multiplePendingDocumentInputRef = useRef<HTMLInputElement>(null);
  const singlePendingDocumentInputRef = useRef<HTMLInputElement>(null);

  const { uploadRequestedFiles } = useDocuments();
  const responsibleUsers = documents?.responsible_users || [];
  const delegatedParty = documents?.delegated_responsible_party;
  const delegationThread = documents?.delegation_thread || [];
  const delegationDepth = documents?.delegation_depth ?? 0;
  const canDelegate = delegationDepth < MAX_DELEGATION_DEPTH;
  const { errorNotification } = useResponse();

  const getSentAndUploadDate = (document: DocumentProgress) => {
    const { created_at: sentDate } = document || {};
    const formattedCreatedDate = getFormattedDate(sentDate);
    return formattedCreatedDate;
  };

  const validateFiles = useCallback((files: File[]) => {
    const filesTooBig = Array.from(files).filter(file => file.size > MAX_DOCUMENT_FILE_SIZE_ALLOWED);
    const filesWithInvalidExtensions = Array.from(files).filter(file => {
      const extension = file.name.split('.').pop();
      return extension ? !validFileTypes.includes(extension.toLowerCase()) : true;
    });
    return { filesTooBig, filesWithInvalidExtensions };
  }, []);

  const handleOpenSelectionModal = useCallback(() => {
    setOpenSelectionModal(true);
  }, [setOpenSelectionModal]);

  const handleFiles = useCallback(
    files => {
      const { filesTooBig, filesWithInvalidExtensions } = validateFiles(files);
      if (isEmpty(filesTooBig) && isEmpty(filesWithInvalidExtensions)) {
        const filesArray = Array.from(files);
        const filesToUploadWithTmpId = (filesArray as File[]).map((file: File) => ({ file, tmpId: uuid(), name: file.name }));
        setFilesToUpload(filesToUploadWithTmpId);
        handleOpenSelectionModal();
      } else {
        filesTooBig.forEach(file => {
          errorNotification(
            `File size exceeds the maximum allowed size of ${MAX_DOCUMENT_SIZE_IN_MB} MB: ${file.name}`
          );
        });
        filesWithInvalidExtensions.forEach(file => {
          errorNotification(FILE_TYPE_NOT_ALLOWED(file.name, validFileTypes.join(', ')));
        });
      }
    },
    [errorNotification, handleOpenSelectionModal, setFilesToUpload, validateFiles]
  );

  const handleFile = useCallback(
    async (fileParam, documentParam) => {
      const { filesTooBig, filesWithInvalidExtensions } = validateFiles(fileParam);
      if (isEmpty(filesTooBig) && isEmpty(filesWithInvalidExtensions)) {
        const { requestId, hmacToken } = taskPortalLinkData || {};
        const requestedFilesIds = documentParam?.id ? [documentParam.id] : [];

        await uploadRequestedFiles({
          droppedFile: fileParam[0],
          requestedFilesIds,
          task,
          setUploadedFiles,
          requestId,
          hmacToken,
        });
      } else {
        filesTooBig.forEach(fileToValidate => {
          errorNotification(
            `File size exceeds the maximum allowed size of ${MAX_DOCUMENT_SIZE_IN_MB} MB: ${fileToValidate.name}`
          );
        });
        filesWithInvalidExtensions.forEach(fileToValidate => {
          errorNotification(FILE_TYPE_NOT_ALLOWED(fileToValidate.name, validFileTypes.join(', ')));
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      errorNotification,
      handleOpenSelectionModal,
      setFilesToUpload,
      validateFiles,
      taskPortalLinkData,
      task,
      setUploadedFiles,
      uploadRequestedFiles,
    ]
  );

  const singleFileInputClicked = useCallback((documentParam: DocumentProgress | null) => {
    setSingleRequestedFileSelected(documentParam);
    singlePendingDocumentInputRef.current?.click();
  }, []);

  const fileSelected = () => {
    if (singlePendingDocumentInputRef?.current?.files?.length) {
      handleFile(singlePendingDocumentInputRef.current.files, singleRequestedFileSelected);
    }
  };

  const filesSelected = () => {
    if (multiplePendingDocumentInputRef?.current?.files?.length) {
      handleFiles(multiplePendingDocumentInputRef.current.files);
    }
  };

  const openDismissDocumentRequestModal = useCallback((document: DocumentProgress) => {
    setDocumentToRemove(document);
    setIsOpenDismissDocumentRequestModal(true);
  }, []);

  const removeDocument = useCallback(
    async (document: DocumentProgress) => {
      try {
        if (!lastTaskId || !document.id) {
          enqueueSnackbar(ERROR_DISMISSING_REQUESTED_DOCUMENT, { variant: 'error' });
          return false;
        }
        const data = {
          task: lastTaskId,
          requested_documents_to_delete: [document.id],
          requested_documents: [],
        };
        const response = await modifyExistingRequest(data);

        if (response?.message === SUCCESS_MODIFYING_INFORMATION_REQUEST) {
          enqueueSnackbar(SUCCESS_DISMISSING_REQUESTED_DOCUMENT, { variant: 'success' });
          return true;
        }
        enqueueSnackbar(ERROR_DISMISSING_REQUESTED_DOCUMENT, { variant: 'error' });
        return false;
      } catch (error) {
        enqueueSnackbar(ERROR_DISMISSING_REQUESTED_DOCUMENT, { variant: 'error' });
        return false;
      } finally {
        setIsOpenDismissDocumentRequestModal(false);
        setDocumentToRemove(null);
        handleGetCurrentInformationRequests();
      }
    },
    [lastTaskId, handleGetCurrentInformationRequests]
  );

  const markDocumentNotApplicable = useCallback(async () => {
    if (!documentToMarkNa?.id) {
      return;
    }
    try {
      setIsMarkingNa(true);
      await filesService.markRequestedFileFulfillment(documentToMarkNa.id, {
        fulfillment_status: naStatus,
        fulfillment_note: naNote.trim() || null,
      });
      enqueueSnackbar('Document marked successfully.', { variant: 'success' });
      setDocumentToMarkNa(null);
      setNaNote('');
      handleGetCurrentInformationRequests();
    } catch (error) {
      Logger.error('Failed to mark document N/A:', error);
      enqueueSnackbar('Failed to update document status.', { variant: 'error' });
    } finally {
      setIsMarkingNa(false);
    }
  }, [documentToMarkNa, naStatus, naNote, handleGetCurrentInformationRequests]);

  const optionsForExtraActionsMenu = useCallback(
    (documentParam: DocumentProgress | null) => [
      {
        label: 'UPLOAD THIS DOCUMENT',
        callback: () => singleFileInputClicked(documentParam),
        isActive: !!taskPortalLinkData,
      },
      {
        label: 'MARK NOT APPLICABLE',
        callback: () => {
          if (documentParam !== null) {
            setDocumentToMarkNa(documentParam);
            setNaStatus('not_applicable');
            setNaNote('');
          }
        },
        isActive: true,
      },
      {
        label: 'DISMISS DOCUMENT REQUEST',
        callback: () => {
          if (documentParam !== null) {
            openDismissDocumentRequestModal(documentParam);
          }
        },
        isActive: true,
      },
    ],
    [singleFileInputClicked, taskPortalLinkData, openDismissDocumentRequestModal]
  );

  const pendingDocumentsTableHeadRows = useMemo(
    () => [
      { id: 'name', label: 'Document Request Name' },
      { id: 'source', label: 'Responsible Party' },
      { id: 'sentUploadDate', label: 'Sent Date' },
      {
        id: 'actions',
        label: '',
        component: 'actions',
        align: 'right',
        options: [
          {
            label: 'UPLOAD DOCUMENTS',
            callback: () => multiplePendingDocumentInputRef.current?.click(),
            isActive: !!taskPortalLinkData,
          },
          {
            label: 'DELEGATE',
            callback: () => setIsDelegateModalOpen(true),
            isActive: !!lastTaskId && canDelegate,
          },
        ],
      },
    ],
    [taskPortalLinkData, lastTaskId, canDelegate]
  );

  const actionsForDismissDocumentRequest = useMemo<Action[]>(
    () => [
      {
        label: 'CANCEL',
        variant: 'outlined',
        disabled: false,
        callback: () => setIsOpenDismissDocumentRequestModal(false),
      },
      {
        label: 'YES, CONFIRM',
        variant: 'contained',
        disabled: false,
        callback: () => {
          if (documentToRemove !== null) {
            removeDocument(documentToRemove);
          }
        },
      },
    ],
    [documentToRemove, removeDocument]
  );

  const uploadToOtherRequestedFile = useCallback(() => {
    const otherRequestedFile = task?.requested_files?.find(item => item.name === OTHER) || {
      id: OTHER,
      name: OTHER,
    };
    const requestedFilesIds = [otherRequestedFile.id];
    filesToUpload.forEach(async fileToUpload => {
      await uploadRequestedFiles({
        droppedFile: fileToUpload.file,
        requestedFilesIds,
        task,
        setUploadedFiles,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesToUpload, setUploadedFiles, task, uploadRequestedFiles]);

  const documentUploadsProps = {
    setOpenSelectionModal,
    openSelectionModal,
    filesToUpload,
    setFilesToUpload,
    uploadRequestedFiles,
    setUploadedFiles: setUploadedFiles ?? (() => {}),
    uploadToOtherRequestedFile,
    taskPortalLinkData: taskPortalLinkData ? {
      ...taskPortalLinkData,
      processType: 'documents',
      portal: 'portal'
    } : undefined,
    requestedFiles: documents?.progress,
    companyDocuments: [],
  };

  const { isOver, handleDragOverOrEnter, handleOnDrop, handleDragLeave } = useDragAndDropFiles({
    handleFiles,
  });

  const renderTableRow = (document: DocumentProgress) => {
    const docDelegationThread = document.delegation_thread || [];

    // Use per-document thread first, fall back to task-level thread.
    // The last entry in the thread is whoever currently sits at the bottom of the
    // delegation chain and is responsible for this document.
    const threadToShow = docDelegationThread.length > 0 ? docDelegationThread : delegationThread;
    const bottomOfChain = threadToShow[threadToShow.length - 1];
    const displayName = bottomOfChain
      ? bottomOfChain.name
      : responsibleUsers.map((user: { full_name: string }) => user.full_name).join(', ');

    // Create tooltip content showing the full delegation thread
    const tooltipContent = threadToShow.length > 0 ? (
      <Box sx={{ p: 1 }}>
        {threadToShow.map((item, index) => (
          <Box key={index} sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', color: 'white' }}>
              {item.role}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'white' }}>
              {item.name}
            </Typography>
            {item.email && (
              <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                {item.email}
              </Typography>
            )}
            {index < threadToShow.length - 1 && (
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', my: 0.5, color: 'white' }}>
                ↓
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    ) : '';

    return (
      <StyledTableRow
        hover
        key={document.id}
        onClick={() => taskPortalLinkData && singleFileInputClicked(document)}
        style={{ cursor: taskPortalLinkData ? 'pointer' : 'not-allowed' }}>
        <StyledTableCell>{document.name}</StyledTableCell>
        <StyledTableCell>
          {threadToShow.length > 0 ? (
            <Tooltip title={tooltipContent} placement="top" arrow>
              <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>
                {displayName}
              </span>
            </Tooltip>
          ) : (
            displayName
          )}
        </StyledTableCell>
        <StyledTableCell>{getSentAndUploadDate(document)}</StyledTableCell>
        <StyledTableCell>
          <Box className={classes.actionMenu} onClick={event => event.stopPropagation()}>
            <ExtraActionsMenu options={optionsForExtraActionsMenu(document)} dotsColor={theme.palette.grey[500]} />
          </Box>
        </StyledTableCell>
      </StyledTableRow>
    );
  };

  const unansweredQuestions = filterDelegatableQuestions(questions || []);
  const hasPendingItems = !isEmpty(unmetRequests) || !isEmpty(unansweredQuestions);

  return (
    <Root>
      {openSelectionModal && <SelectionModal {...documentUploadsProps} open={openSelectionModal} />}
      <ConfirmationDialog
        open={isOpenDismissDocumentRequestModal}
        title="Do you confirm that you want to dismiss this document request?"
        onClose={() => setIsOpenDismissDocumentRequestModal(false)}
        actions={actionsForDismissDocumentRequest}>
        <span>
          This modification will affect the original request to the company user, eliminating it from its portal.
        </span>
      </ConfirmationDialog>
      <Dialog open={Boolean(documentToMarkNa)} onClose={() => setDocumentToMarkNa(null)}>
        <DialogTitle>Mark document as not applicable</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {documentToMarkNa?.name}
          </Typography>
          <TextField
            select
            fullWidth
            margin="dense"
            label="Status"
            value={naStatus}
            onChange={event => setNaStatus(event.target.value as RequestedFileFulfillmentStatus)}
            id="analyst-mark-fulfillment-status"
          >
            <MenuItem value="not_applicable">Not applicable</MenuItem>
            <MenuItem value="not_available">Not available</MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            label="Note"
            value={naNote}
            onChange={event => setNaNote(event.target.value)}
            multiline
            minRows={2}
            placeholder="Optional note"
            id="analyst-mark-fulfillment-note"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentToMarkNa(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={markDocumentNotApplicable}
            disabled={isMarkingNa}
            id="analyst-confirm-mark-fulfillment-button"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <AdminDelegateModal
        open={isDelegateModalOpen}
        onClose={() => setIsDelegateModalOpen(false)}
        onSuccess={() => {
          setIsDelegateModalOpen(false);
          handleGetCurrentInformationRequests();
        }}
        taskId={lastTaskId ?? undefined}
        requestedFiles={unmetRequests ?? []}
        requestedQuestions={questions}
        delegationDepth={delegationDepth}
      />

      {/* Show pending documents table if there are unmet requests */}
      {!isEmpty(unmetRequests) && (
        <>
          <Typography variant="h3">Pending Document Requests</Typography>
          <input ref={multiplePendingDocumentInputRef} hidden type="file" multiple onChange={filesSelected} />
          <input ref={singlePendingDocumentInputRef} hidden type="file" multiple onChange={fileSelected} />
          <TableContainer component={Paper}>
            <Table
              style={{ minWidth: 650, border: isOver ? `2px dashed ${theme.palette.grey[300]}` : 'none' }}
              size="small"
              aria-label="documents table">
              <EnhancedTableHead
                onRequestSort={() => {}}
                order="asc"
                orderBy="name"
                columns={pendingDocumentsTableHeadRows as EnhancedTableHeadColumn[]}
                files={currentDocuments?.files ?? []}
                selectedFiles={selectedFiles ?? []}
                setSelectedFiles={setSelectedFiles}
              />
              <TableBody
                onDragOver={handleDragOverOrEnter}
                onDrop={handleOnDrop}
                onDragLeave={handleDragLeave}
                style={{ backgroundColor: isOver ? theme.palette.grey[100] : theme.palette.white }}>
                {unmetRequests?.map(renderTableRow)}
              </TableBody>
            </Table>
          </TableContainer>
          <br />
        </>
      )}

      {/* Show delegate button if there are pending items (questions only) but no document table showing */}
      {isEmpty(unmetRequests) && hasPendingItems && lastTaskId && canDelegate && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setIsDelegateModalOpen(true)}
          >
            Delegate Request
          </Button>
        </Box>
      )}
    </Root>
  );
};

export default PendingDocumentTable;
